import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useAutoresponder } from '@/hooks/useAutoresponder';
import type { Email } from '@/types/email';

// Event for email auth errors - allows cross-component communication
export const EMAIL_AUTH_ERROR_EVENT = 'email-auth-error';

export interface EmailAuthError {
  accountId: string;
  emailAddress: string;
}

export const useEmailSync = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { sendAutoresponse } = useAutoresponder();
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentFolder, setCurrentFolder] = useState<'inbox' | 'sent' | 'draft'>('inbox');
  const [totalEmailsOnServer, setTotalEmailsOnServer] = useState<number>(0);
  const [emailAuthError, setEmailAuthError] = useState<EmailAuthError | null>(null);

  // E-Mails aus der Datenbank laden (funktionierende Version)
  const loadEmails = useCallback(async (folder: 'inbox' | 'sent' | 'draft' = 'inbox', reset = true) => {
    if (!user) return [];

    if (reset) {
      setLoading(true);
      setHasMore(true);
      setCurrentFolder(folder);
    }

    try {
      // Nur E-Mails von Konten mit funktionierenden IMAP-Servern laden
      const { data: accounts } = await supabase
        .from('email_accounts')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .not('last_sync_at', 'is', null); // Nur Konten die bereits erfolgreich synchronisiert wurden

      if (!accounts || accounts.length === 0) {
        if (reset) setEmails([]);
        if (folder === 'inbox') {
          toast({
            title: "Keine funktionierenden E-Mail-Konten",
            description: "Bitte konfigurieren Sie ein funktionierendes E-Mail-Konto mit korrekten IMAP-Einstellungen.",
            variant: "destructive",
          });
        }
        return [];
      }

      const accountIds = accounts.map(account => account.id);

      let query = supabase
        .from('emails')
        .select('*')
        .eq('user_id', user.id)
        .in('account_id', accountIds); // Nur E-Mails von funktionierenden Konten

      if (folder === 'sent') {
        query = query.eq('status', 'sent');
      } else if (folder === 'draft') {
        query = query.eq('status', 'draft');
      } else {
        query = query.eq('status', 'received');
      }

      const limit = reset ? 20 : 10; // Erste Ladung: 20, weitere: 10
      const offset = reset ? 0 : emails.length;
      
      const { data, error } = await query
        .order('received_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      const formattedEmails = (data as any[])?.map(item => ({
        ...item,
        to_addresses: Array.isArray(item.to_addresses) ? item.to_addresses : [],
        cc_addresses: Array.isArray(item.cc_addresses) ? item.cc_addresses : [],
        bcc_addresses: Array.isArray(item.bcc_addresses) ? item.bcc_addresses : [],
        attachments: Array.isArray(item.attachments) ? item.attachments : [],
        labels: Array.isArray(item.labels) ? item.labels : []
      })) || [];

      if (reset) {
        setEmails(formattedEmails);
      } else {
        setEmails(prev => [...prev, ...formattedEmails]);
      }
      
      // Prüfe ob weitere E-Mails vorhanden sind - korrigierte Logik
      setHasMore(formattedEmails.length === limit);
      
      return formattedEmails;
      
    } catch (error) {
      console.error('Error loading emails:', error);
      toast({
        title: "Fehler",
        description: "E-Mails konnten nicht geladen werden.",
        variant: "destructive",
      });
      return [];
    } finally {
      if (reset) setLoading(false);
    }
  }, [user, toast, emails.length]);

  // Weitere E-Mails live laden für Infinite Scroll
  const loadMoreEmails = useCallback(async () => {
    if (loadingMore || !hasMore || loading) return;
    
    setLoadingMore(true);
    try {
      const newEmails = await loadEmails(currentFolder, false);
      console.log(`✅ Loaded ${newEmails?.length || 0} more emails, hasMore: ${newEmails && newEmails.length > 0}`);
    } catch (error) {
      console.error('Error loading more emails:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadEmails, currentFolder, loadingMore, hasMore, loading]);

  // E-Mail-Synchronisation vom IMAP-Server in die Datenbank
  const syncEmails = useCallback(async () => {
    if (!user) return;

    setSyncing(true);
    try {
      const { data: accounts, error: accountsError } = await supabase
        .from('email_accounts')
        .select('id, email_address')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (accountsError) throw accountsError;

      if (!accounts || accounts.length === 0) {
        toast({
          title: "Keine Konten",
          description: "Bitte fügen Sie zuerst ein E-Mail-Konto hinzu.",
          variant: "destructive",
        });
        return;
      }

      let totalSynced = 0;
      let serverTotalEmails = 0;
      for (const account of accounts) {
        try {
          const { data, error } = await supabase.functions.invoke('sync-emails', {
            body: { accountId: account.id }
          });

          if (error) {
            console.error(`Sync-Fehler für ${account.email_address}:`, error);
          } else if (data?.code === 'AUTH_FAILED') {
            // Email password changed - trigger re-auth
            console.error(`Auth-Fehler für ${account.email_address}:`, data.message);
            const authError: EmailAuthError = {
              accountId: account.id,
              emailAddress: account.email_address,
            };
            setEmailAuthError(authError);
            // Dispatch custom event for notification bell
            window.dispatchEvent(new CustomEvent(EMAIL_AUTH_ERROR_EVENT, { detail: authError }));
            toast({
              title: "E-Mail-Anmeldung erforderlich",
              description: `Das Passwort für ${account.email_address} ist ungültig.`,
              variant: "destructive",
            });
          } else {
            console.log('📊 Sync response data:', data);
            totalSynced += data?.synced_count || 0;
            // Addiere die Gesamtanzahl aller E-Mails auf dem Server
            serverTotalEmails += data?.total_emails || 0;
          }
        } catch (err) {
          console.error(`Sync-Fehler für ${account.email_address}:`, err);
        }
      }

      // Aktualisiere die Gesamtanzahl der E-Mails auf dem Server
      setTotalEmailsOnServer(serverTotalEmails);

      setLastSyncTime(new Date());
      
      if (totalSynced > 0) {
        toast({
          title: "Synchronisation erfolgreich",
          description: `${totalSynced} neue E-Mails synchronisiert.`,
        });
        await loadEmails(currentFolder);
      } else {
        toast({
          title: "Synchronisation abgeschlossen",
          description: "Keine neuen E-Mails gefunden.",
        });
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast({
        title: "Synchronisation fehlgeschlagen",
        description: "E-Mails konnten nicht synchronisiert werden.",
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  }, [user, toast, loadEmails, currentFolder]);

  // E-Mail als gelesen markieren
  const markAsRead = useCallback(async (emailId: string) => {
    try {
      const { error } = await supabase
        .from('emails')
        .update({ is_read: true })
        .eq('id', emailId);

      if (error) throw error;

      setEmails(prev => prev.map(email => 
        email.id === emailId ? { ...email, is_read: true } : email
      ));
    } catch (error) {
      console.error('Error marking email as read:', error);
    }
  }, []);

  // E-Mail Stern-Status ändern
  const toggleStarred = useCallback(async (emailId: string, isStarred: boolean) => {
    try {
      const { error } = await supabase
        .from('emails')
        .update({ is_starred: !isStarred })
        .eq('id', emailId);

      if (error) throw error;

      setEmails(prev => prev.map(email => 
        email.id === emailId ? { ...email, is_starred: !isStarred } : email
      ));
    } catch (error) {
      console.error('Error updating email:', error);
    }
  }, []);

  // E-Mail löschen (mit IMAP-Löschung)
  const deleteEmail = useCallback(async (emailId: string, options?: { 
    messageId?: string; 
    accountId?: string;
    showToast?: boolean;
  }) => {
    try {
      // Optimistic UI Update - E-Mail sofort aus der Liste entfernen
      const deletedEmail = emails.find(e => e.id === emailId);
      setEmails(prev => prev.filter(email => email.id !== emailId));

      // Wenn IMAP-Löschung gewünscht und möglich
      if (options?.messageId && options?.accountId) {
        const { error: imapError } = await supabase.functions.invoke('delete-email-imap', {
          body: { 
            emailId, 
            messageId: options.messageId, 
            accountId: options.accountId 
          }
        });

        if (imapError) {
          console.error('IMAP delete error:', imapError);
          // Trotzdem fortfahren - DB-Löschung erfolgt in Edge Function
        }
      } else {
        // Nur aus Datenbank löschen
        const { error } = await supabase
          .from('emails')
          .delete()
          .eq('id', emailId);

        if (error) throw error;
      }

      if (options?.showToast !== false) {
        toast({
          title: "E-Mail gelöscht",
          description: "Die E-Mail wurde erfolgreich gelöscht.",
        });
      }

      return { success: true, deletedEmail };
    } catch (error) {
      console.error('Error deleting email:', error);
      toast({
        title: "Fehler",
        description: "E-Mail konnte nicht gelöscht werden.",
        variant: "destructive",
      });
      // E-Mail wieder hinzufügen bei Fehler
      await loadEmails(currentFolder);
      return { success: false };
    }
  }, [toast, emails, loadEmails, currentFolder]);

  // Realtime-Subscription für sofortige E-Mail-Updates
  useEffect(() => {
    if (!user) return;

    // Initiales Laden
    loadEmails();

    // Realtime-Channel für E-Mail-Änderungen
    const channel = supabase
      .channel('emails-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'emails',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('📬 Neue E-Mail erhalten:', payload.new);
          const newEmail = payload.new as any;
          
          // Nur hinzufügen wenn es zum aktuellen Ordner passt
          const matchesFolder = 
            (currentFolder === 'inbox' && newEmail.status === 'received') ||
            (currentFolder === 'sent' && newEmail.status === 'sent') ||
            (currentFolder === 'draft' && newEmail.status === 'draft');
          
          if (matchesFolder) {
            const formattedEmail = {
              ...newEmail,
              to_addresses: Array.isArray(newEmail.to_addresses) ? newEmail.to_addresses : [],
              cc_addresses: Array.isArray(newEmail.cc_addresses) ? newEmail.cc_addresses : [],
              bcc_addresses: Array.isArray(newEmail.bcc_addresses) ? newEmail.bcc_addresses : [],
              attachments: Array.isArray(newEmail.attachments) ? newEmail.attachments : [],
              labels: Array.isArray(newEmail.labels) ? newEmail.labels : []
            };
            
            // Am Anfang der Liste einfügen (neueste zuerst)
            setEmails(prev => {
              // Prüfe ob E-Mail bereits existiert
              if (prev.some(e => e.id === formattedEmail.id)) {
                return prev;
              }
              return [formattedEmail, ...prev];
            });
          }

          // Trigger autoresponder for new received emails
          if (newEmail.status === 'received' && newEmail.from_address) {
            sendAutoresponse({
              from_address: newEmail.from_address,
              subject: newEmail.subject || '',
              body_text: newEmail.body_text,
              body_html: newEmail.body_html,
              account_id: newEmail.account_id
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'emails',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('📝 E-Mail aktualisiert:', payload.new);
          const updatedEmail = payload.new as any;
          
          setEmails(prev => prev.map(email => 
            email.id === updatedEmail.id 
              ? {
                  ...updatedEmail,
                  to_addresses: Array.isArray(updatedEmail.to_addresses) ? updatedEmail.to_addresses : [],
                  cc_addresses: Array.isArray(updatedEmail.cc_addresses) ? updatedEmail.cc_addresses : [],
                  bcc_addresses: Array.isArray(updatedEmail.bcc_addresses) ? updatedEmail.bcc_addresses : [],
                  attachments: Array.isArray(updatedEmail.attachments) ? updatedEmail.attachments : [],
                  labels: Array.isArray(updatedEmail.labels) ? updatedEmail.labels : []
                }
              : email
          ));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'emails',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🗑️ E-Mail gelöscht:', payload.old);
          const deletedEmail = payload.old as any;
          setEmails(prev => prev.filter(email => email.id !== deletedEmail.id));
        }
      )
      .subscribe((status) => {
        console.log('📡 E-Mail Realtime Status:', status);
      });

    // Periodische IMAP-Synchronisation im Hintergrund (alle 60 Sekunden)
    const syncInterval = setInterval(async () => {
      try {
        const { data: accounts } = await supabase
          .from('email_accounts')
          .select('id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .not('last_sync_at', 'is', null);

        if (accounts && accounts.length > 0) {
          for (const account of accounts) {
            await supabase.functions.invoke('sync-emails', {
              body: { accountId: account.id }
            });
          }
        }
      } catch (error) {
        console.error('Background sync error:', error);
      }
    }, 60000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(syncInterval);
    };
  }, [user, currentFolder]);

  // Clear email auth error
  const clearEmailAuthError = useCallback(() => {
    setEmailAuthError(null);
  }, []);

  return {
    emails,
    loading,
    syncing,
    loadingMore,
    hasMore,
    lastSyncTime,
    totalEmailsOnServer,
    emailAuthError,
    clearEmailAuthError,
    loadEmails,
    loadMoreEmails,
    syncEmails,
    markAsRead,
    toggleStarred,
    deleteEmail
  };
};